/**
 * Utility functions for handling cached data with version-based cache busting
 */

// Store for version information
const versionCache = {
  lastChecked: 0,
  currentVersion: null,
  checkInterval: 5 * 60 * 1000 // 5 minutes
};

// Cache for the actual data
const dataCache = {
  lastData: null,
  lastUrl: null
};

/**
 * Fetch with timeout
 * @param {string} url - The URL to fetch
 * @param {Object} options - Fetch options
 * @param {number} timeout - Timeout in milliseconds
 * @returns {Promise<Response>} The fetch response
 */
const fetchWithTimeout = async (url, options = {}, timeout = 1000) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);
  
  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal
    });
    clearTimeout(timeoutId);
    return response;
  } catch (error) {
    clearTimeout(timeoutId);
    throw error;
  }
};

/**
 * Get the current version from the API
 * @returns {Promise<string>} The current version hash
 */
export const getVersion = async () => {
  const now = Date.now();
  
  // If we have a recent version and it's within our check interval, use it
  if (versionCache.currentVersion && 
      (now - versionCache.lastChecked) < versionCache.checkInterval) {
    return versionCache.currentVersion;
  }
  
  try {
    // Use fetchWithTimeout to prevent hanging on version API call
    const response = await fetchWithTimeout('/racing_results/api/version', {
      headers: {
        'Accept': 'application/json',
        'Cache-Control': 'no-cache'
      }
    }, 1000);
    
    if (response.ok) {
      const data = await response.json();
      versionCache.currentVersion = data.version;
      versionCache.lastChecked = now;
      return data.version;
    }
  } catch (error) {
    console.warn('Failed to get version (timeout or error):', error);
  }
  
  // If we have a previous version, use it even if it's old
  if (versionCache.currentVersion) {
    console.log('Using previous version due to API failure');
    return versionCache.currentVersion;
  }
  
  // Last resort fallback to timestamp
  console.log('No version available, using timestamp');
  return now.toString();
};

/**
 * Fetch data with cache busting
 * @param {string} url - The URL to fetch
 * @param {Object} options - Fetch options
 * @returns {Promise<any>} The fetched data
 */
export const fetchWithCacheBusting = async (url, options = {}) => {
  let versionedUrl;
  
  try {
    const version = await getVersion();
    // Add version parameter to URL
    const separator = url.includes('?') ? '&' : '?';
    versionedUrl = `${url}${separator}v=${version}`;
  } catch (error) {
    console.warn('Error getting version, using URL without version:', error.message);
    versionedUrl = url;
  }
  
  console.log(`Fetching ${versionedUrl} with cache busting`);
  const startTime = performance.now();
  
  try {
    // Use regular fetch for data (no timeout)
    const response = await fetch(versionedUrl, options);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    // Check if the response is from cache
    const fromCache = response.headers.get('x-cache') === 'HIT' || 
                      response.headers.get('cf-cache-status') === 'HIT' ||
                      (performance.now() - startTime < 50); // Likely from cache if very fast
    
    if (fromCache) {
      console.log('Response loaded from cache');
    }
    
    // Parse the response as JSON
    const data = await response.json();
    console.log(`Loaded ${data.length} records in ${(performance.now() - startTime).toFixed(2)}ms`);
    
    // Store in cache
    dataCache.lastData = data;
    dataCache.lastUrl = url;
    
    return data;
  } catch (error) {
    console.error('Error fetching data:', error.message);
    throw error;
  }
};